import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getMonitoringSystem,
  getMonitoringServices,
  getMonitoringSockets,
  getMonitoringLocalNodes,
  getMonitoringHistory,
  toggleNodeAlerts,
} from '../services/api.service';
import {
  CpuChipIcon,
  ServerStackIcon,
  SignalIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  BoltIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CircleStackIcon,
  GlobeAltIcon,
  WifiIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';

import { PageTitle } from '@trinetra/shared';
import { useAuth } from '../contexts/AuthContext';


const POLL_INTERVAL = 10_000;

/* ── Formatters ─────────────────────────────────── */
function fmtUptime(sec: number) {
  if (!sec || sec < 0) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtBytes(b: number) {
  if (!b || b === 0) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtBps(b: number) {
  if (!b || b === 0) return '0 bps';
  const bits = b * 8;
  if (bits < 1000) return `${bits.toFixed(0)} bps`;
  if (bits < 1000000) return `${(bits / 1000).toFixed(1)} Kbps`;
  return `${(bits / 1000000).toFixed(2)} Mbps`;
}

function fmtRate(v: number) {
  if (!v || v === 0) return '0';
  if (v < 1000) return `${v.toFixed(0)}/s`;
  return `${(v / 1000).toFixed(1)}K/s`;
}

function timeAgo(ts: number) {
  if (!ts) return 'Never';
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/* ── Small Reusable Components ──────────────────── */
function ProgressBar({ percent }: { percent: number }) {
  const cls = percent > 90 ? 'bg-red-500' : percent > 75 ? 'bg-amber-500' : percent > 60 ? 'bg-yellow-500' : 'bg-brand';
  return (
    <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--muted)' }}>
      <div className={`h-full rounded-full transition-all duration-500 ${cls}`} style={{ width: `${Math.min(percent, 100)}%` }} />
    </div>
  );
}

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg shadow-card" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid var(--border)' }}>
        <span className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--brand)' }}>{icon}</span>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function StatCard({ label, value, sub, accent = false }: { label: string; value: any; sub?: string; accent?: boolean }) {
  return (
    <div className="rounded-lg p-3" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
      <div className="text-[10px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>{label}</div>
      <div className={`text-xl font-bold font-mono ${accent ? 'text-brand' : ''}`} style={accent ? {} : { color: 'var(--foreground)' }}>{value}</div>
      {sub && <div className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>{sub}</div>}
    </div>
  );
}

function NoData({ msg }: { msg?: string }) {
  return (
    <div className="rounded-lg p-8 text-center text-sm" style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
      {msg || 'No data available'}
    </div>
  );
}

function TabLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-2" style={{ borderColor: 'var(--muted)', borderTopColor: 'var(--brand)' }} />
    </div>
  );
}

/* ── Sparkline Chart ─────────────────────────────── */
function Sparkline({ data, color, label, unit = '', maxOverride }: { data: any[]; color: string; label: string; unit?: string; maxOverride?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<{ idx: number; px: number } | null>(null);

  if (!data || data.length < 2) return <NoData msg={`No ${label} data yet`} />;
  const entries = data.map((d: any) => ({ t: d.ts ?? d.t ?? 0, v: typeof d === 'number' ? d : (d.value ?? d.v ?? 0) }));
  const vals = entries.map((e) => e.v);
  const min = Math.min(...vals);
  const max = maxOverride ?? Math.max(...vals);
  const range = max - min || 1;
  const w = 300, h = 80, pad = 2;

  const coords = entries.map((e, i) => ({
    x: pad + (i / (entries.length - 1)) * (w - 2 * pad),
    y: h - pad - ((e.v - min) / range) * (h - 2 * pad),
  }));
  const points = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const fillPoints = `${pad},${h - pad} ${points} ${w - pad},${h - pad}`;
  const last = vals[vals.length - 1];

  const handleMouseMove = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect || entries.length < 2) return;
    const pxX = e.clientX - rect.left;
    const ratio = pxX / rect.width;
    const idx = Math.max(0, Math.min(entries.length - 1, Math.round(ratio * (entries.length - 1))));
    setHover({ idx, px: pxX });
  };

  const fmtTime = (ts: number) => {
    const msTs = ts > 1e12 ? ts : ts * 1000;
    return new Date(msTs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const hoverEntry = hover ? entries[hover.idx] : null;
  const hoverCoord = hover ? coords[hover.idx] : null;

  return (
    <div className="relative" ref={containerRef} onMouseMove={handleMouseMove} onMouseLeave={() => setHover(null)} style={{ cursor: 'crosshair' }}>
      {label && <div className="text-[10px] font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>{label}</div>}
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height: h }} preserveAspectRatio="none">
        <polygon points={fillPoints} fill={color} fillOpacity={0.15} />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        {hoverCoord && (
          <>
            <line x1={hoverCoord.x} y1={pad} x2={hoverCoord.x} y2={h - pad} stroke={color} strokeWidth="0.8" strokeDasharray="3,2" opacity="0.7" />
            <circle cx={hoverCoord.x} cy={hoverCoord.y} r="3" fill={color} stroke="#fff" strokeWidth="1" />
          </>
        )}
      </svg>
      {hover && hoverEntry && (
        <div className="absolute z-10 px-2.5 py-1.5 rounded-md shadow-lg pointer-events-none text-[10px] font-mono"
          style={{ background: 'var(--card)', border: '1px solid var(--border)', color: 'var(--foreground)', left: Math.min(hover.px, (containerRef.current?.offsetWidth || 200) - 120), top: 16, minWidth: 100 }}>
          <div style={{ color }}>{hoverEntry.v.toFixed(2)}{unit}</div>
          <div style={{ color: 'var(--muted-foreground)' }}>{fmtTime(hoverEntry.t)}</div>
        </div>
      )}
      <div className="flex justify-between text-[9px] font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
        <span>{entries.length > 0 ? fmtTime(entries[0].t) : ''}</span>
        <span style={{ color }}>Current: {last.toFixed(1)}{unit}</span>
        <span>{entries.length > 0 ? fmtTime(entries[entries.length - 1].t) : ''}</span>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════ */
const TAB_IDS = ['performance', 'history', 'local-nodes'] as const;

export default function SystemMonitor() {
  const navigate = useNavigate();
  // Tab lives in the URL (`?tab=local-nodes`) so it can be linked to — the offline-node notice
  // sends users straight to Local Nodes. Same pattern as /user-management?tab=. An unknown or
  // missing value falls back to Performance.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = TAB_IDS.includes(requestedTab as any) ? (requestedTab as string) : 'performance';
  const setActiveTab = (tab: string) => setSearchParams({ tab }, { replace: true });
  const [historyRange, setHistoryRange] = useState('1h');

  const [perfData, setPerfData] = useState<any>(null);
  const [perfLoading, setPerfLoading] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [servicesData, setServicesData] = useState<any>(null);
  const [nodesData, setNodesData] = useState<any>(null);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [copyMenuOpen, setCopyMenuOpen] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const copyMenuRef = useRef<HTMLDivElement>(null);

  const { hasPermission } = useAuth()

  // Close copy menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target as Node)) {
        setCopyMenuOpen(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchPerformance = useCallback(async (loader = false) => {
    if (loader) setPerfLoading(true);
    try {
      const [sysRes, svcRes, sockRes] = await Promise.allSettled([
        getMonitoringSystem(),
        getMonitoringServices(),
        getMonitoringSockets(),
      ]);
      setPerfData(sysRes.status === 'fulfilled' ? sysRes.value : null);
      setServicesData({
        services: svcRes.status === 'fulfilled' ? svcRes.value : null,
        sockets: sockRes.status === 'fulfilled' ? sockRes.value : null,
      });
    } catch (e) { console.warn('[Monitor] perf:', e); }
    finally { setPerfLoading(false); }
  }, []);

  const fetchHistory = useCallback(async (range: string, loader = false) => {
    if (loader) setHistoryLoading(true);
    try { setHistoryData(await getMonitoringHistory(range)); } catch (e) { console.warn('[Monitor] history:', e); }
    finally { setHistoryLoading(false); }
  }, []);

  const fetchNodes = useCallback(async (loader = false) => {
    if (loader) setNodesLoading(true);
    try { setNodesData(await getMonitoringLocalNodes()); } catch (e) { console.warn('[Monitor] nodes:', e); }
    finally { setNodesLoading(false); }
  }, []);

  const fetchForTab = useCallback((tab: string, loader = true) => {
    switch (tab) {
      case 'performance': return fetchPerformance(loader);
      case 'history': return fetchHistory(historyRange, loader);
      case 'local-nodes': return fetchNodes(loader);
    }
  }, [fetchPerformance, fetchHistory, fetchNodes, historyRange]);

  useEffect(() => {
    fetchForTab(activeTab, true);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = window.setInterval(() => fetchForTab(activeTab, false), POLL_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [activeTab, fetchForTab]);

  useEffect(() => {
    if (activeTab === 'history') fetchHistory(historyRange, true);
  }, [historyRange, activeTab, fetchHistory]);

  const cpu = perfData?.cpu;
  const mem = perfData?.memory;
  const disk = perfData?.disk;
  const net = perfData?.network;
  const proc = perfData?.process;

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setCopyMenuOpen(null);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleToggleAlerts = async (nodeId: string) => {
    try {
      await toggleNodeAlerts(nodeId);
      await fetchNodes(false);
    } catch (e) { console.warn('Toggle alerts failed:', e); }
  };

  const tabs = [
    { id: 'performance', label: 'Performance', icon: <BoltIcon className="h-4 w-4" /> },
    { id: 'history', label: 'History', icon: <ClockIcon className="h-4 w-4" /> },
    { id: 'local-nodes', label: 'Sites', icon: <ComputerDesktopIcon className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-3">
      <PageTitle title="Platform Health" subtitle="Cloud server performance, services & sites">
        {activeTab === 'history' && (
          <div className="flex gap-1">
            {['1h', '6h', '12h', '24h'].map((r) => (
              <button key={r} onClick={() => setHistoryRange(r)}
                className="h-7 px-2.5 text-xs font-medium rounded-md transition-colors"
                style={{ background: historyRange === r ? 'var(--brand)' : 'var(--input-background)', color: historyRange === r ? '#fff' : 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                {r}
              </button>
            ))}
          </div>
        )}
      </PageTitle>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 overflow-x-auto" style={{ borderBottom: '1px solid var(--border)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap"
            style={{ borderBottom: activeTab === t.id ? '2px solid var(--brand)' : '2px solid transparent', color: activeTab === t.id ? 'var(--brand)' : 'var(--muted-foreground)' }}>
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ═══ TAB: Performance ═══ */}
      {activeTab === 'performance' && (perfLoading && !perfData ? <TabLoader /> :
        <div className="space-y-3">
          {/* System Metrics Card */}
          <div className="rounded-lg overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            <div className="px-4 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)', background: 'var(--input-background)' }}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                <ServerStackIcon className="h-4 w-4" style={{ color: 'var(--brand)' }} />
                <span className="text-sm font-semibold" style={{ color: 'var(--foreground)' }}>Cloud Server</span>
                {cpu?.model && <span className="text-[10px] font-mono hidden sm:inline" style={{ color: 'var(--muted-foreground)' }}>({cpu.model.split(' ').slice(0, 3).join(' ')})</span>}
              </div>
              <span className="text-[10px] font-mono" style={{ color: 'var(--muted-foreground)' }}>Uptime: {fmtUptime(proc?.uptime_seconds)}</span>
            </div>
            <div className="p-4 space-y-4">
              {/* CPU / MEM / DISK / LOAD / SWAP */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    <CpuChipIcon className="h-3 w-3" /> CPU
                  </div>
                  <div className="text-lg font-mono font-bold" style={{ color: 'var(--foreground)' }}>{(cpu?.usage_percent ?? 0).toFixed(1)}%</div>
                  <ProgressBar percent={cpu?.usage_percent || 0} />
                  <div className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>{cpu?.core_count} cores</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    <CircleStackIcon className="h-3 w-3" /> Memory
                  </div>
                  <div className="text-lg font-mono font-bold" style={{ color: 'var(--foreground)' }}>{(mem?.usage_percent ?? 0).toFixed(1)}%</div>
                  <ProgressBar percent={mem?.usage_percent || 0} />
                  <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{fmtBytes(mem?.used_bytes)}/{fmtBytes(mem?.total_bytes)}</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    <CircleStackIcon className="h-3 w-3" /> Disk
                  </div>
                  <div className="text-lg font-mono font-bold" style={{ color: 'var(--foreground)' }}>{(disk?.usage_percent ?? 0).toFixed(1)}%</div>
                  <ProgressBar percent={disk?.usage_percent || 0} />
                  <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{fmtBytes(disk?.available_bytes)} free</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    <ArrowPathIcon className="h-3 w-3" /> Load Avg
                  </div>
                  <div className="text-sm font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{cpu?.load_avg_1m?.toFixed(2)}</div>
                  <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{cpu?.load_avg_5m?.toFixed(2)} / {cpu?.load_avg_15m?.toFixed(2)}</div>
                  <div className="text-[9px]" style={{ color: 'var(--muted-foreground)' }}>1m / 5m / 15m</div>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1 text-[10px] font-medium" style={{ color: 'var(--muted-foreground)' }}>
                    <CircleStackIcon className="h-3 w-3" /> Swap
                  </div>
                  <div className="text-sm font-mono font-semibold" style={{ color: 'var(--foreground)' }}>{fmtBytes(mem?.swap_used_bytes)}</div>
                  <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>/ {fmtBytes(mem?.swap_total_bytes)}</div>
                </div>
              </div>

              {/* Network */}
              {net?.interfaces && net.interfaces.length > 0 && (
                <div>
                  <div className="flex items-center gap-1 text-[10px] font-medium mb-2" style={{ color: 'var(--muted-foreground)' }}>
                    <GlobeAltIcon className="h-3 w-3" /> Network Interfaces
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: 'var(--input-background)' }}>
                          <th className="text-left px-3 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Interface</th>
                          <th className="text-right px-2 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>RX (Mbps)</th>
                          <th className="text-right px-2 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>TX (Mbps)</th>
                          <th className="text-right px-2 py-1.5 text-[10px] uppercase font-semibold hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>RX Pkt/s</th>
                          <th className="text-right px-2 py-1.5 text-[10px] uppercase font-semibold hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>TX Pkt/s</th>
                        </tr>
                      </thead>
                      <tbody>
                        {net.interfaces.map((iface: any) => (
                          <tr key={iface.device} className="border-t" style={{ borderColor: 'var(--border)' }}>
                            <td className="px-3 py-1.5 font-mono font-medium" style={{ color: 'var(--foreground)' }}>{iface.device}</td>
                            <td className="text-right px-2 py-1.5 font-mono text-emerald-400">{((iface.recv_bytes_per_sec || 0) * 8 / 1_000_000).toFixed(2)}</td>
                            <td className="text-right px-2 py-1.5 font-mono text-blue-400">{((iface.send_bytes_per_sec || 0) * 8 / 1_000_000).toFixed(2)}</td>
                            <td className="text-right px-2 py-1.5 font-mono hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>{(iface.recv_packets_per_sec || 0).toLocaleString()}</td>
                            <td className="text-right px-2 py-1.5 font-mono hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>{(iface.send_packets_per_sec || 0).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cloud Backend (Node.js) Service Metrics */}
          {servicesData?.services?.cloud_backend && (() => {
            const cb = servicesData.services.cloud_backend;
            return (
              <SectionCard icon={<CpuChipIcon className="h-4 w-4" />} title="Cloud Backend (Node.js)">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="space-y-1">
                    <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>CPU</div>
                    <div className="text-lg font-mono font-bold" style={{ color: 'var(--foreground)' }}>{(cb.cpu_percent ?? 0).toFixed(1)}%</div>
                    <ProgressBar percent={cb.cpu_percent || 0} />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Memory</div>
                    <div className="text-lg font-mono font-bold" style={{ color: 'var(--foreground)' }}>{cb.memory_mb?.toFixed(0)} MB</div>
                    <div className="text-[9px] font-mono" style={{ color: 'var(--muted-foreground)' }}>Heap: {cb.heap_used_mb}MB / {cb.heap_total_mb}MB</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Disk I/O</div>
                    <div className="text-[11px] font-mono" style={{ color: 'var(--muted-foreground)' }}>R: {fmtBytes(cb.disk_read_bytes_per_sec)}/s</div>
                    <div className="text-[11px] font-mono" style={{ color: 'var(--muted-foreground)' }}>W: {fmtBytes(cb.disk_write_bytes_per_sec)}/s</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[9px] uppercase tracking-wider font-medium" style={{ color: 'var(--muted-foreground)' }}>Network</div>
                    <div className="text-[11px] font-mono" style={{ color: 'var(--muted-foreground)' }}>Sent: {fmtRate(cb.net_packets_sent_per_sec)} pkt</div>
                    <div className="text-[11px] font-mono" style={{ color: 'var(--muted-foreground)' }}>Recv: {fmtRate(cb.net_packets_recv_per_sec)} pkt</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 pt-2 text-[10px] font-mono" style={{ borderTop: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
                  <span>Threads: {cb.threads || '—'}</span>
                  <span>Open FDs: {cb.open_fds || '—'}</span>
                  <span>Uptime: {fmtUptime(cb.uptime_seconds)}</span>
                  <span>Node.js: {cb.node_version}</span>
                </div>
              </SectionCard>
            );
          })()}

          {/* WebSocket Details */}
          {servicesData?.sockets && (
            <SectionCard icon={<WifiIcon className="h-4 w-4" />} title="Cloud ⇄ Site WebSockets">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard label="Total Sites" value={servicesData.sockets.summary?.total_nodes || 0} accent />
                <StatCard label="Connected" value={servicesData.sockets.summary?.connected || 0} sub="active tunnels" />
                <StatCard label="Disconnected" value={servicesData.sockets.summary?.disconnected || 0} />
                <StatCard label="Zombie Detections" value={servicesData.sockets.summary?.total_zombie_detections || 0} />
              </div>
              {servicesData.sockets.nodes?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ background: 'var(--input-background)' }}>
                        <th className="text-left px-3 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Site</th>
                        <th className="text-center px-2 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>Status</th>
                        <th className="text-center px-2 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>RX</th>
                        <th className="text-center px-2 py-1.5 text-[10px] uppercase font-semibold" style={{ color: 'var(--muted-foreground)' }}>TX</th>
                        <th className="text-center px-2 py-1.5 text-[10px] uppercase font-semibold hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>Errors</th>
                        <th className="text-center px-2 py-1.5 text-[10px] uppercase font-semibold hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servicesData.sockets.nodes.map((n: any) => (
                        <tr key={n.nodeId} className="border-t" style={{ borderColor: 'var(--border)' }}>
                          <td className="px-3 py-1.5 font-mono font-medium" style={{ color: 'var(--foreground)' }}>{n.nodeName || n.nodeId?.slice(0, 8)}</td>
                          <td className="text-center px-2 py-1.5">
                            <span className={`inline-block w-2 h-2 rounded-full ${n.connected ? 'bg-emerald-400' : 'bg-red-400'}`} />
                          </td>
                          <td className="text-center px-2 py-1.5 font-mono text-emerald-400">{fmtBps(n.bytesReceivedPerSec)}</td>
                          <td className="text-center px-2 py-1.5 font-mono text-blue-400">{fmtBps(n.bytesSentPerSec)}</td>
                          <td className="text-center px-2 py-1.5 font-mono hidden sm:table-cell" style={{ color: n.errorCount > 0 ? 'var(--destructive)' : 'var(--muted-foreground)' }}>{n.errorCount || 0}</td>
                          <td className="text-center px-2 py-1.5 font-mono hidden sm:table-cell" style={{ color: 'var(--muted-foreground)' }}>{n.lastActivity ? timeAgo(n.lastActivity) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <NoData msg="No sites connected" />}
            </SectionCard>
          )}

          {/* Nodes Summary */}
          {servicesData?.services?.nodes_summary && (
            <SectionCard icon={<ServerStackIcon className="h-4 w-4" />} title="Sites Summary">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <StatCard label="Total Sites" value={servicesData.services.nodes_summary.total || 0} accent />
                <StatCard label="Online" value={servicesData.services.nodes_summary.online || 0} />
                <StatCard label="Offline" value={servicesData.services.nodes_summary.offline || 0} />
                <StatCard label="Tunnels Active" value={servicesData.services.nodes_summary.tunnels_active || 0} />
                <StatCard label="Active Sessions" value={servicesData.services.nodes_summary.total_active_sessions || 0} />
              </div>
            </SectionCard>
          )}
        </div>
      )}

      {/* ═══ TAB: Performance History ═══ */}
      {activeTab === 'history' && (historyLoading && !historyData ? <TabLoader /> :
        <div className="space-y-3">
          {historyData ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Sparkline data={historyData.cpu} color="#3b82f6" label="CPU %" unit="%" maxOverride={100} />
              <Sparkline data={historyData.memory} color="#8b5cf6" label="Memory %" unit="%" maxOverride={100} />
              <Sparkline data={historyData.disk} color="#a855f7" label="Disk %" unit="%" maxOverride={100} />
              <Sparkline data={historyData.network_recv} color="#10b981" label="Network RX" unit=" B/s" />
              <Sparkline data={historyData.network_send} color="#f59e0b" label="Network TX" unit=" B/s" />
            </div>
          ) : <NoData msg="History data is being collected. Check back in a few minutes." />}
        </div>
      )}

      {/* ═══ TAB: Local Nodes ═══ */}
      {activeTab === 'local-nodes' && (nodesLoading && !nodesData ? <TabLoader /> :
        <div className="space-y-3">
          {nodesData?.nodes?.length > 0 ? (
            <div className="space-y-3">
              {nodesData.nodes.map((node: any) => (
                <div key={node.nodeId} className="rounded-lg overflow-hidden" style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  {/* Node Header */}
                  <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2" style={{ borderBottom: '1px solid var(--border)', background: 'var(--input-background)' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <ComputerDesktopIcon className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--brand)' }} />
                      <span className="text-sm font-semibold truncate" style={{ color: 'var(--foreground)' }}>{node.name}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 ${node.status === 'online' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/10 text-red-400'
                        }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${node.status === 'online' ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`} />
                        {node.status === 'online' ? 'Online' : 'Offline'}
                      </span>
                      {node.tunnelActive && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-blue-500/10 text-blue-400 flex-shrink-0">
                          <SignalIcon className="h-3 w-3" /> Tunnel
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Copy Button with click-based dropdown */}
                      <div className="relative" ref={copyMenuOpen === node.nodeId ? copyMenuRef : undefined}>
                        <button
                          onClick={(e) => { e.stopPropagation(); setCopyMenuOpen(copyMenuOpen === node.nodeId ? null : node.nodeId); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all"
                          style={{ background: 'var(--input-background)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}
                        >
                          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                          <span className="hidden sm:inline">Copy</span>
                          <ChevronDownIcon className={`h-3 w-3 transition-transform ${copyMenuOpen === node.nodeId ? 'rotate-180' : ''}`} />
                        </button>
                        {copyMenuOpen === node.nodeId && (
                          <div className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-lg py-1 min-w-[150px]"
                            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(node.nodeId, `${node.nodeId}-id`); }}
                              className="w-full text-left px-3 py-2 text-[11px] transition-colors flex items-center gap-2"
                              style={{ color: 'var(--foreground)' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-background)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                              <IdentificationIcon className="h-3.5 w-3.5" style={{ color: 'var(--muted-foreground)' }} />
                              {copiedField === `${node.nodeId}-id` ? <><CheckIcon className="h-3.5 w-3.5 text-emerald-400" /> Copied!</> : 'Node ID'}
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleCopy(node.name, `${node.nodeId}-name`); }}
                              className="w-full text-left px-3 py-2 text-[11px] transition-colors flex items-center gap-2"
                              style={{ color: 'var(--foreground)' }}
                              onMouseEnter={e => (e.currentTarget.style.background = 'var(--input-background)')}
                              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            >
                              <DocumentDuplicateIcon className="h-3.5 w-3.5" style={{ color: 'var(--muted-foreground)' }} />
                              {copiedField === `${node.nodeId}-name` ? <><CheckIcon className="h-3.5 w-3.5 text-emerald-400" /> Copied!</> : 'Node Name'}
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Inspect Button */}
                      <button onClick={() => navigate(`/node-monitor/${node.nodeId}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-[11px] font-medium transition-all"
                        style={{ background: 'var(--brand)', color: '#fff' }}>
                        <MagnifyingGlassIcon className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Inspect</span>
                      </button>


                      {hasPermission('monitoring.edit') && (
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>Alerts</span>
                          <button onClick={() => handleToggleAlerts(node.nodeId)}
                            className="relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0"
                            style={{ background: node.alertsEnabled ? 'var(--brand)' : 'var(--muted)' }}
                            title={node.alertsEnabled ? 'Click to disable alerts' : 'Click to enable alerts'}>
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${node.alertsEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                      )}

                    </div>
                  </div>

                  {/* Node Metrics - Responsive Grid */}
                  < div className="p-4" >
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                      {/* Last Seen */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <ClockIcon className="h-3 w-3" /> Last Seen
                        </div>
                        <div className="text-sm font-mono font-semibold mt-0.5" style={{ color: 'var(--foreground)' }}>
                          {node.lastSeen ? timeAgo(node.lastSeen) : 'Never'}
                        </div>
                      </div>
                      {/* CPU */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <CpuChipIcon className="h-3 w-3" /> CPU
                        </div>
                        {node.metrics ? (
                          <div>
                            <div className="text-sm font-mono font-bold mt-0.5" style={{ color: 'var(--foreground)' }}>{node.metrics.cpu_percent?.toFixed(1)}%</div>
                            <ProgressBar percent={node.metrics.cpu_percent || 0} />
                          </div>
                        ) : <div className="text-sm font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>—</div>}
                      </div>
                      {/* Memory */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <CircleStackIcon className="h-3 w-3" /> Memory
                        </div>
                        {node.metrics ? (
                          <div>
                            <div className="text-sm font-mono font-bold mt-0.5" style={{ color: 'var(--foreground)' }}>{node.metrics.memory_percent?.toFixed(1)}%</div>
                            <ProgressBar percent={node.metrics.memory_percent || 0} />
                            <div className="text-[8px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{node.metrics.memory_used_mb}MB/{node.metrics.memory_total_mb}MB</div>
                          </div>
                        ) : <div className="text-sm font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>—</div>}
                      </div>
                      {/* Disk */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <CircleStackIcon className="h-3 w-3" /> Disk
                        </div>
                        {node.metrics && node.metrics.storage_total_gb > 0 ? (
                          <div>
                            <div className="text-sm font-mono font-bold mt-0.5" style={{ color: 'var(--foreground)' }}>{((node.metrics.storage_used_gb / node.metrics.storage_total_gb) * 100).toFixed(1)}%</div>
                            <ProgressBar percent={(node.metrics.storage_used_gb / node.metrics.storage_total_gb) * 100} />
                            <div className="text-[8px] font-mono" style={{ color: 'var(--muted-foreground)' }}>{node.metrics.storage_used_gb?.toFixed(1)}GB/{node.metrics.storage_total_gb?.toFixed(1)}GB</div>
                          </div>
                        ) : <div className="text-sm font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>—</div>}
                      </div>
                      {/* Network */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <GlobeAltIcon className="h-3 w-3" /> Network
                        </div>
                        {node.metrics ? (
                          <div className="font-mono text-[11px] mt-0.5 space-y-0.5">
                            <div className="text-emerald-400">↓ {fmtBps(node.metrics.network_rx_bytes_per_sec)}</div>
                            <div className="text-blue-400">↑ {fmtBps(node.metrics.network_tx_bytes_per_sec)}</div>
                          </div>
                        ) : <div className="text-sm font-mono mt-0.5" style={{ color: 'var(--muted-foreground)' }}>—</div>}
                      </div>
                      {/* Alerts */}
                      <div className="rounded-md p-2.5" style={{ background: 'var(--input-background)', border: '1px solid var(--border)' }}>
                        <div className="text-[9px] uppercase tracking-wider font-medium flex items-center gap-1" style={{ color: 'var(--muted-foreground)' }}>
                          <SignalIcon className="h-3 w-3" /> Alerts
                        </div>
                        <div className={`text-sm font-semibold mt-0.5 ${node.alertsEnabled ? 'text-emerald-400' : 'text-red-400'}`}>
                          {node.alertsEnabled ? 'Enabled' : 'Disabled'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <NoData msg="No local nodes registered" />
          }
        </div >
      )}

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-center gap-1 text-[10px] font-mono" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }}>
        <ArrowPathIcon className="h-3 w-3" />
        Auto-refreshing every {POLL_INTERVAL / 1000}s
      </div>
    </div >
  );
}
